import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  FaBuilding,
  FaEnvelope,
  FaIdCard,
  FaMapMarkerAlt,
  FaPhone,
  FaUser,
} from "react-icons/fa";
import CustomerAvatar from "./CustomerAvatar";

const show = (value, fallback = "—") =>
  value === 0 || value === "0" ? "0" : value ? String(value) : fallback;

const money = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  return `₹ ${amount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

const formatType = (value) =>
  show(value, "Party")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const VendorAvatarHoverCard = ({ vendor }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const info = vendor?.info || vendor || {};
  const vendorId = vendor?.value || info?._id;

  useEffect(() => {
    if (!vendorId) setOpen(false);
  }, [vendorId]);

  useEffect(() => {
    if (!open) return undefined;

    const closeOnOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const address = [
    info?.addressLine1,
    info?.city,
    info?.state,
    info?.pincode,
    info?.country,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <span ref={rootRef} className="relative inline-block shrink-0">
      <button
        type="button"
        disabled={!vendorId}
        onClick={() => vendorId && setOpen((current) => !current)}
        className="rounded-2xl outline-none focus-visible:ring-4 focus-visible:ring-violet-400 disabled:cursor-not-allowed disabled:opacity-60"
        aria-haspopup="dialog"
        aria-expanded={open}
        title={vendorId ? "View vendor details" : "Select a vendor"}
      >
        <CustomerAvatar
          size="md"
          tone="violet"
          title={vendorId ? "Vendor details" : "Select a vendor"}
          className={vendorId ? "" : "opacity-50"}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.16 }}
            className="absolute -right-2 z-[1000] mt-2 min-w-[18rem] sm:min-w-[22rem]"
            role="dialog"
            aria-label="Vendor details"
          >
            <span
              className="absolute -top-2 right-4 z-10 h-4 w-4 rotate-45 border border-b-0 border-r-0 border-slate-200 bg-[#9481ce] shadow-sm"
              aria-hidden
            />
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
              <div className="border border-[#9481ce] bg-[#9481ce] px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-white">Vendor</span>
                  {(info?.shortName || info?.vendorType) && (
                    <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-700">
                      {show(info?.shortName, formatType(info?.vendorType))}
                    </span>
                  )}
                  <span className="ml-auto text-[10px] text-white/90">
                    {formatType(info?.vendorType)}
                  </span>
                </div>
              </div>

              <div className="px-3 py-2 text-sm text-slate-700">
                <div className="mb-2">
                  <div className="flex items-center gap-2 truncate font-semibold text-slate-900">
                    <FaUser className="text-xs text-slate-400" />
                    {show(info?.vendorName || vendor?.label)}
                  </div>
                  {info?.companyName && (
                    <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500">
                      <FaBuilding className="text-[11px] text-slate-400" />
                      {info.companyName}
                    </div>
                  )}
                </div>

                <div className="mb-2 space-y-1 text-xs text-slate-500">
                  {info?.contact && (
                    <div className="flex items-center gap-2">
                      <FaPhone className="text-slate-400" /> {info.contact}
                    </div>
                  )}
                  {info?.email && (
                    <div className="flex min-w-0 items-center gap-2">
                      <FaEnvelope className="shrink-0 text-slate-400" />
                      <span className="truncate">{info.email}</span>
                    </div>
                  )}
                  {info?.gst && (
                    <div className="flex items-center gap-2">
                      <FaIdCard className="text-slate-400" /> GST: {info.gst}
                    </div>
                  )}
                </div>

                {address && (
                  <div className="mb-3 flex items-start gap-2 text-xs text-slate-600">
                    <FaMapMarkerAlt className="mt-0.5 shrink-0 text-slate-400" />
                    <span>{address}</span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <Metric label="Fine Gold" value={show(info?.fineGold)} tone="gold" />
                  <Metric label="Fine Silver" value={show(info?.fineSilver)} tone="silver" />
                  <Metric label="Advance" value={money(info?.advanceAmount)} tone="amount" />
                  <Metric label="Balance" value={money(info?.balanceAmount)} tone="danger" />
                </div>
              </div>

              <div className="border-t bg-slate-50 px-3 py-2 text-right">
                <button type="button" onClick={() => setOpen(false)} className="rounded-md border bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-100">
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </span>
  );
};

const Metric = ({ label, value, tone }) => {
  const tones = {
    gold: "bg-gradient-to-br from-amber-50 to-amber-100 text-amber-700 ring-1 ring-amber-200",
    silver: "bg-gradient-to-br from-slate-50 to-slate-100 text-slate-800 ring-1 ring-slate-200",
    amount: "bg-gradient-to-br from-emerald-50 to-emerald-100 text-emerald-700 ring-1 ring-emerald-200",
    danger: "bg-gradient-to-br from-rose-50 to-rose-100 text-rose-700 ring-1 ring-rose-200",
  };

  return (
    <div className={`rounded-xl p-3 ${tones[tone]}`}>
      <div className="text-xs font-medium opacity-90">{label}</div>
      <div className="mt-1 text-xs font-semibold tracking-tight">{value}</div>
    </div>
  );
};

export default VendorAvatarHoverCard;
